/**
 * Header dropdown listing every committed Cleared List report by creation
 * date so the user can flip between past reports without leaving the page.
 * Selecting one loads it read-only into the match table. The page defaults
 * the selection to the most recent report on load.
 *
 * Mirrors the Adesa Run List sibling (components/wholesale-auction/
 * RunSwitcher.tsx) — same dropdown mechanics (click-outside + Escape close)
 * and trigger styling — but typed for WholesaleRunSummary and labelled by
 * the run's creation date (started_at) rather than its id.
 */
import React, { useEffect, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"

import { WholesaleRunSummary } from "../../api/wholesale"
import { THEME } from "../../config/theme"
import AuctionStatusPill from "../wholesale-auction/AuctionStatusPill"

interface Props {
  runs: WholesaleRunSummary[]
  current: WholesaleRunSummary | null
  onSelect: (runId: number) => void
  onOpen?: () => void
  // Fill the row (mobile: the switcher sits full-width on its own line).
  fullWidth?: boolean
}

export default function ClearedRunSwitcher({
  runs,
  current,
  onSelect,
  onOpen,
  fullWidth = false,
}: Props) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false)
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [open])

  const toggle = () => {
    const next = !open
    setOpen(next)
    if (next) onOpen?.()
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        display: fullWidth ? "block" : "inline-block",
        ...(fullWidth && { width: "100%" }),
      }}
    >
      <button
        type="button"
        onClick={toggle}
        style={fullWidth ? { ...buttonStyle, width: "100%" } : buttonStyle}
      >
        <span>{current ? formatDateTime(current.started_at) : "Select run"}</span>
        {current && <AuctionStatusPill status={current.status} />}
        {/* Push the chevron to the right edge when the trigger is full-width. */}
        <ChevronDown
          size={14}
          strokeWidth={2}
          style={fullWidth ? { marginLeft: "auto" } : undefined}
        />
      </button>

      {open && (
        <div
          style={fullWidth ? { ...menuStyle, width: "100%", minWidth: 0 } : menuStyle}
          role="menu"
        >
          {runs.length === 0 ? (
            <div style={emptyStyle}>No reports yet</div>
          ) : (
            runs.map((run) => (
              <button
                key={run.id}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  onSelect(run.id)
                }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    THEME.colors.neutral.light.background
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLButtonElement).style.backgroundColor =
                    run.id === current?.id ? THEME.colors.neutral.light.background : "transparent"
                }}
                style={menuItemStyle(run.id === current?.id)}
              >
                <span>{formatDateTime(run.started_at)}</span>
                <AuctionStatusPill status={run.status} />
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—"
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

// Secondary-style trigger — matches the auction RunSwitcher trigger verbatim.
const buttonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.sm,
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
}

const menuStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  minWidth: 220,
  maxHeight: "60vh",
  overflowY: "auto",
  backgroundColor: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  zIndex: 100,
  overflowX: "hidden",
}

function menuItemStyle(selected: boolean): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: THEME.spacing.md,
    width: "100%",
    textAlign: "left",
    padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
    border: "none",
    backgroundColor: selected ? THEME.colors.neutral.light.background : "transparent",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: selected ? 600 : 400,
    color: THEME.colors.neutral.light.textPrimary,
    fontFamily: THEME.fonts.primary,
    transition: "background-color 0.1s",
  }
}

const emptyStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  fontStyle: "italic",
}
